import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './SendersPage.css';

function SendersPage({ accountId: currentAccountId, onSwitchAccount }) {
  const navigate = useNavigate();
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | in_campaign | available

  const loadSenders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/linkedin/accounts`, {
        timeout: 20000,
      });
      if (res.data.success) {
        setSenders(res.data.senders || []);
      } else {
        setError(res.data.message || 'Failed to fetch senders');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSenders();
  }, [loadSenders]);

  const filteredSenders = useMemo(() => {
    const kw = search.trim().toLowerCase();
    return senders.filter(s => {
      if (filter === 'in_campaign' && s.active_campaigns === 0) return false;
      if (filter === 'available' && s.active_campaigns > 0) return false;
      if (!kw) return true;
      return (
        (s.name || '').toLowerCase().includes(kw) ||
        (s.email || '').toLowerCase().includes(kw) ||
        (s.public_identifier || '').toLowerCase().includes(kw)
      );
    });
  }, [senders, search, filter]);

  const handleSelectSender = (sender) => {
    if (onSwitchAccount) {
      onSwitchAccount(sender.account_id);
    }
  };

  const handleConfigureLimits = (e, sender) => {
    e.stopPropagation();
    if (onSwitchAccount) {
      onSwitchAccount(sender.account_id);
    }
    navigate('/settings');
  };

  const handleConnectAccount = () => {
    navigate('/connect-sender');
  };

  return (
    <div className="senders-page">
      <div className="senders-container">
        <div className="senders-header">
          <h1>LinkedIn Accounts</h1>
          <div className="senders-subtitle">
            The LinkedIn accounts are called <strong>senders</strong> when put in a campaign.
            Switch between them to view each sender's campaigns and leads.
          </div>
        </div>

        <div className="senders-toolbar">
          <div className="senders-toolbar-left">
            <input
              type="text"
              className="senders-search"
              placeholder="Search senders by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="senders-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="in_campaign">In campaign</option>
              <option value="available">Available</option>
            </select>
          </div>
          <div className="senders-toolbar-right">
            <button className="btn-connect-account" onClick={handleConnectAccount}>
              + Connect account
            </button>
          </div>
        </div>

        {error && <div className="senders-alert senders-alert-error">{error}</div>}

        <div className="senders-table-wrapper">
          <table className="senders-table">
            <thead>
              <tr>
                <th>LinkedIn Account</th>
                <th>Status</th>
                <th>Sending limits</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="senders-empty">Loading senders...</td>
                </tr>
              )}
              {!loading && filteredSenders.length === 0 && (
                <tr>
                  <td colSpan={4} className="senders-empty">
                    {senders.length === 0 ? 'No LinkedIn accounts connected yet.' : 'No senders match the filter.'}
                  </td>
                </tr>
              )}
              {!loading && filteredSenders.map(sender => {
                const isActive = sender.account_id === currentAccountId;
                return (
                  <tr
                    key={sender.account_id}
                    className={isActive ? 'senders-row active' : 'senders-row'}
                    onClick={() => handleSelectSender(sender)}
                  >
                    <td>
                      <div className="senders-account-cell">
                        <div className="senders-account-avatar">
                          {(sender.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="senders-account-info">
                          <div className="senders-account-name">
                            {sender.name}
                            {isActive && <span className="senders-badge-current">current</span>}
                          </div>
                          <div className="senders-account-meta">
                            <span className="senders-provider-icon">in</span>
                            {sender.premium ? 'LinkedIn Premium' : 'LinkedIn Basic'}
                            {sender.email && <span className="senders-email"> · {sender.email}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {sender.active_campaigns > 0 ? (
                        <span className="senders-status-pill senders-status-in">
                          In {sender.active_campaigns} campaign{sender.active_campaigns > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="senders-status-pill senders-status-available">
                          Available
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="senders-limits-placeholder">—</span>
                    </td>
                    <td className="senders-actions-cell">
                      <button
                        className="btn-configure-limits"
                        onClick={(e) => handleConfigureLimits(e, sender)}
                      >
                        Configure limits
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="senders-footer">
          Showing 1-{filteredSenders.length} of {senders.length}
        </div>
      </div>
    </div>
  );
}

export default SendersPage;
